/*!
* Define Impulse Response Convolution Reverb properties and behaviour.
*/
import axios from 'axios';

export default class IrConvolutionReverb {
	/* DEFAULT LOGIC (METHODS) THAT SHOULD BE IMPLEMENTED IN EACH PLUGIN */

	//Used for getting current values for save
	getValues() {
		let values = {};
		values.impulseResponse = this.impulseResponse;
		values.predelay = this.predelay;
		values.dry = this.dry;
		values.wet = this.wet;
		values.masterGain = this.masterGain;
		return values;
	}

	// Used for setting stored values.
	// For each value, we check to see if it was passed in the 
	// values parameter before setting it to a default value
	// We'll do the same thing with a ternary for all the properties.
	async setValues(values) {
		if (typeof values == 'undefined' || Object.keys(values).length === 0) { // if null or empty object passed

			// Set all user controllable settings to defaults
			this.impulseResponse = this.defaultImpulseResponse;
			this.predelay = this.defaultPredelay;
			this.dry = this.defaultDry;
			this.wet = this.defaultWet;

			// Master gain
			this.masterGain = this.defaultMasterGain;
		} else {
			if (this.isDebug) {
				console.debug('Set IRC reverb values', { values, initialized: this.initialized });
			}

			// Set all user controllable settings to defaults
			this.impulseResponse = values.hasOwnProperty('impulseResponse') ? values.impulseResponse : this.defaultImpulseResponse;
			this.predelay = values.hasOwnProperty('predelay') ? values.predelay : this.defaultPredelay;
			this.dry = values.hasOwnProperty('dry') ? values.dry : this.defaultDry;
			this.wet = values.hasOwnProperty('wet') ? values.wet : this.defaultWet;

			// Master gain
			this.masterGain = values.hasOwnProperty('masterGain') ? values.masterGain : this.defaultMasterGain;

			//COMMENT: setValues should not only set defaults to variables, but also set values to audio nodes, if initialized
			await this.updateImpulseResponse(this.impulseResponse);
			this.updatePredelay(this.predelay);
			this.updateDry(this.dry);
			this.updateWet(this.wet);
			this.updateMasterGain(this.masterGain);
		}

	}

	//Rows: array of rows (visually each row will be stacked vertically), inside array of elements - they will be stacked horizontally in a row - affectors (value changers) or indicators
	//Availale kinds: 'indicator', 'affector'
	//Available types for affectors: 'round-button' (min, max, step), 'slider' (min, max, step),
	//Available types for indicators: 'vu-meter' (min, max, orientation(vertical, horizontal))
	//Indicator can show values in pure value (like db) or percent, but better in pure value. Examples: { value: -15 }, { percent: 50 }
	getUI() {
		return {
			rows: [
				[
					{
						rows: [
							[
								{
									kind: 'affector', type: 'select-menu', width: 75, color: '#4dffae', title: 'Impulse Responses',
									options: this.getIrOptions(),
									setValue: async (value) => { return await this.updateImpulseResponse(value) },
									getValue: () => { return this.impulseResponse }
								},
							]
						],
					},
				],
				[
					{
						rows: [
							[
								{
									kind: 'affector', type: 'round-button', width: 75, color: '#4dffae', title: 'Predelay', min: this.minPredelay, max: this.maxPredelay, step: 1, unit: 'ms',
									setValue: (value) => { return this.updatePredelay(value) },
									getValue: () => { return this.predelay }
								},
							]
						],
						orientation: 'vertical'
					},
					{
						rows: [
							[
								{
									kind: 'affector', type: 'round-button', width: 75, color: '#4dffae', title: 'Dry', min: this.minDry, max: this.maxDry, step: 1, unit: '%',
									setValue: (value) => { return this.updateDry(value) },
									getValue: () => { return this.dry }
								},
								{
									kind: 'affector', type: 'round-button', width: 75, color: '#4dffae', title: 'Wet', min: this.minWet, max: this.maxWet, step: 1, unit: '%',
									setValue: (value) => {
										return this.updateWet(value)
									},
									getValue: () => { return this.wet }
								}
							]
						],
						orientation: 'vertical'
					},
					{
						rows: [
							[
								{
									kind: 'affector', type: 'round-button', width: 100, title: 'Master Gain', min: -30, max: 0, step: 0.1, unit: 'dB', round: 1,
									setValue: (value) => { return this.updateMasterGain(value) },
									getValue: () => { return this.masterGain }
								},
							]
						],
						orientation: 'vertical'
					},
					{
						kind: 'indicator', type: 'vu-meter', orientation: 'vertical', title: 'In', min: -60, max: 3, getValue: () => {
							return {
								value: this.preMetering
							}
						}
					},
					{
						kind: 'indicator', type: 'vu-meter', orientation: 'vertical', title: 'Out', min: -60, max: 3, getValue: () => {
							return {
								value: this.postMetering
							}
						}
					}
				]
			],
			width: 860
		}
	}

	/**
	 * Create nodes and connect them
	 * 
	 * @returns object containing input and output node
	 */
	async getAudioPipeline(opts) {
		this.ctx = opts?.audioContext || new AudioContext();

		// Create pre analyser node
		this.preAnalyserNode = this.ctx.createAnalyser(); // The input audio analyser

		// Input gain node
		this.inputGainNode = this.ctx.createGain();

		// Output gain node
		this.outputGainNode = this.ctx.createGain();

		// Master gain node
		this.masterGainNode = this.ctx.createGain();

		// Create post analyser node
		this.postAnalyserNode = this.ctx.createAnalyser(); // The output audio analyser

		// Dry signal node
		this.dryNode = this.ctx.createGain();

		// Reverb signal node
		this.wetNode = this.ctx.createGain();

		// Create convolver node
		this.convolverNode = this.ctx.createConvolver();

		// Create delay node to control reverb predelay
		this.delayNode = this.ctx.createDelay(this.maxDelayTime);

		/**
		 * Connect nodes
		 */
		// Connect dry signal (dryNode) to masterGainNode
		this.preAnalyserNode.connect(this.dryNode);
		this.dryNode.connect(this.masterGainNode);

		// Connect wet signal (all reverb nodes) to masterGainNode
		this.preAnalyserNode.connect(this.convolverNode);
		this.convolverNode.connect(this.delayNode);
		this.delayNode.connect(this.wetNode);
		this.wetNode.connect(this.masterGainNode);

		// Master gain to output
		this.masterGainNode.connect(this.outputGainNode);
		this.outputGainNode.connect(this.postAnalyserNode);

		this.inputNode = this.preAnalyserNode;
		this.outputNode = this.postAnalyserNode;

		await this.init();

		// To input will be connected source (with other effects), output will be connected to destination (or next effect)
		return {
			input: this.preAnalyserNode,
			output: this.postAnalyserNode
		}
	}

	// Called on playing state change (1 - playing, 0 - stop)
	setPlayState(value) {
		this.initialized = !!value;
		this.playing = !!value;

		if (this.isDebug) {
			console.debug('Set play state changed', { value });
		}

		if (value)
			this.updateMeters();
	}

	constructor(opts) {
		this.ctx = opts.context;
		this.player = opts.player;

		// Show console messages
		this.isDebug = opts.isDebug;

		if (this.isDebug) {
			console.log('Creating IR Convolution Reverb... ');
		}

		this.connected = false;

		// Pre gain for solo/mute
		this.bandPreGain = 1;

		// The max delay time of the delay node in seconds
		this.maxDelayTime = .5;

		// Settings
		this.impulseResponse;
		this.predelay;
		this.dry;
		this.wet;
		this.masterGain;

		// Default user controllable settings
		this.defaultImpulseResponse = 'block_inside.wav'; // Free IR's downloaded from https://www.voxengo.com/impulses/
		this.defaultPredelay = 0;
		this.defaultDry = 100;
		this.defaultWet = 50;
		this.defaultMasterGain = 0;

		// Max inupt values
		this.maxGain = 5;
		this.maxPredelay = 500;
		this.maxDry = 100;
		this.maxWet = 100;

		// Min inupt values
		this.minGain = 0;
		this.minPredelay = 0;
		this.minDry = 0;
		this.minWet = 0;

		// Analyser properties
		this.fttSize = 2048; // bytes to allocate for data
		this.preMetering = null;
		this.postMetering = null;

		this.initialized = false;

		this.preInit();
	}

	/* PLUGIN LOGIC */

	// Add some basic functionality for before the user is playing an audio file
	preInit() {
		// Test setting values
		// let values = JSON.parse('{"impulseResponse":"cement_blocks_1.wav","predelay":139,"dry":0.56,"wet":0.96,"masterGain":3}');
		this.setValues();
	}

	// This is the last second logic that needs to execute for the plugin to work
	async init() {
		await this.initNodes(); // Use the values on compressor object to set values of the nodes

		this.initPreAnalyser();
		this.initPostAnalyser();

		this.initialized = true;
	}

	// Initialize values in nodes
	async initNodes() {
		await this.loadImpulseResponse();
		this.dryNode.gain.setValueAtTime(this.dry, this.ctx.currentTime);
		this.wetNode.gain.setValueAtTime(this.wet, this.ctx.currentTime);

		this.delayNode.delayTime.setValueAtTime(this.predelay / 1000, this.ctx.currentTime);

		// Initialize pre analyser node
		this.preAnalyserNode.fftSize = this.fttSize;
		this.preFloatTimeData = new Float32Array(this.preAnalyserNode.frequencyBinCount);
		this.preAnalyserNode.getFloatTimeDomainData(this.preFloatTimeData);

		// Initialize post analyser node
		this.postAnalyserNode.fftSize = this.fttSize;
		this.postFloatTimeData = new Float32Array(this.postAnalyserNode.frequencyBinCount);
		this.postAnalyserNode.getFloatTimeDomainData(this.postFloatTimeData);

		// Initialize master gain node value
		this.masterGainNode.gain.setValueAtTime(this.masterGain, this.ctx.currentTime);
	}

	/**
	 * Load an impulse response into the convolverNode
	 */
	async loadImpulseResponse() {
		// this.convolverNode.buffer = this.ctx.createBuffer(); // Empty buffer
		if (this.isDebug) {
			console.log('loading IR: ' + this.impulseResponse);
		}

		// Load impulse response from file
		return new Promise(res => {
			axios.get(require('@/assets/impulse-responses/' + this.impulseResponse), {
				responseType: 'blob'
			}).then(response => {
				let fr = new FileReader();
				fr.readAsArrayBuffer(response.data);
				fr.onloadend = (evt) => {
					return this.ctx.decodeAudioData(evt.target.result, (buffer) => {
						this.convolverNode.buffer = buffer;

						//Need to be ensured promise will be executed only when audio source fully loaded
						res(buffer);
					});
				};
			});
		});
	}

	async updateImpulseResponse(impulseResponse) {
		if (this.isDebug) {
			console.log('updating impulse reponse to ' + impulseResponse);
		}

		this.impulseResponse = impulseResponse;

		if (!this.initialized) return;

		await this.loadImpulseResponse();
	}

	/** 
	 * Get the name/value pairs that will create the options elements in the select menu
	 */
	getIrOptions() {
		// The list of audio files in the impulse response directory. It would be better to get this programmatically, but I don't know how.
		let files = ['block_inside.wav', 'bottle_hall.wav', 'cement_blocks_1.wav', 'cement_blocks_2.wav', 'chateau_de_logne,_outside.wav', 'conic_long_echo_hall.wav',
			'deep_space.wav', 'derlon_sanctuary.wav', 'direct_cabinet_n1.wav', 'direct_cabinet_n2.wav', 'direct_cabinet_n3.wav', 'direct_cabinet_n4.wav', 'five_columns.wav',
			'five_columns_long.wav', 'french_18th_century_salon.wav', 'going_home.wav', 'greek_7_echo_hall.wav', 'highly_damped_large_room.wav', 'in_the_silo.wav',
			'in_the_silo_revised.wav', 'large_bottle_hall.wav', 'large_long_echo_hall.wav', 'large_wide_echo_hall.wav', 'masonic_lodge.wav', 'musikvereinsaal.wav', 'narrow_bumpy_space.wav',
			'nice_drum_room.wav', 'on_a_star.wav', 'parking_garage.wav', 'rays.wav', 'right_glass_triangle.wav', 'ruby_room.wav', 'scala_milan_opera_hall.wav', 'small_drum_room.wav',
			'small_prehistoric_cave.wav', 'st_nicolaes_church.wav', 'trig_room.wav', 'vocal_duo.wav'];

		let titleFilenameArray = [];

		// Use filename to create a title for the Impulse Response
		files.forEach(element => {
			titleFilenameArray.push({
				name: this.titleCase(element),
				value: element
			});
		});
		return titleFilenameArray;
	}

	/**
	 * Convert an audio file name into a title
	 */
	titleCase(str) {
		str = str.split('.')[0];
		str = str.toLowerCase().split('_');
		for (let i = 0; i < str.length; i++) {
			str[i] = str[i].charAt(0).toUpperCase() + str[i].slice(1);
		}
		return str.join(' ');
	}

	updatePredelay(value) {
		this.predelay = value;

		if (!this.initialized) return;

		// Update the delay time. Convert milliseconds to seconds.
		this.delayNode.delayTime.setValueAtTime(this.predelay / 1000, this.ctx.currentTime);
		// console.log(JSON.stringify(this.getValues()));
	}

	updateDry(value) {
		this.dry = value;
		if (!this.initialized) return;

		// Update the dry signal node gain value
		this.dryNode.gain.setValueAtTime(this.dry/100, this.ctx.currentTime);
	}

	updateWet(value) {
		this.wet = value;
		if (!this.initialized) return;

		// Update the wet signal node gain value
		this.wetNode.gain.setValueAtTime(this.wet/100, this.ctx.currentTime);
	}

	initPreAnalyser() {
		// Analyzer for metering
		this.preAnalyserNode.fftSize = this.fttSize;

		this.preFloatTimeData = new Float32Array(this.preAnalyserNode.frequencyBinCount);
		this.preAnalyserNode.getFloatTimeDomainData(this.preFloatTimeData);
	}

	initPostAnalyser() {
		// Analyzer for metering
		this.postAnalyserNode.fftSize = this.fttSize;

		this.postFloatTimeData = new Float32Array(this.postAnalyserNode.frequencyBinCount);
		this.postAnalyserNode.getFloatTimeDomainData(this.postFloatTimeData);
	}

	/**
	 * Bypass the plugin
	 */
	bypass() {
		if (this.$bypass.attr('data-bypassed') == 'true') {
			this.connected = true;
			this.$bypass.text('On').attr('data-bypassed', 'false');
		} else {
			this.connected = false;
			this.$bypass.text('Off').attr('data-bypassed', 'true');
		}
	}

	updateMasterGain(value) {
		this.masterGain = value;

		this.convertDecibelsToGain(value);

		if (!this.initialized) return;

		// Update the node value
		this.masterGainNode.gain.setValueAtTime(this.convertDecibelsToGain(value), this.ctx.currentTime);
	}

	// The meters are acting as peak meters
	updateMeters() {
		// Schedule the next update
		if (this.playing) {
			requestAnimationFrame(() => this.updateMeters()); // Recursive
		}

		this.updatePreMeters();
		this.updatePostMeters();
	}

	updatePreMeters() {
		// Get the new frequency data
		this.preAnalyserNode.getFloatTimeDomainData(this.preFloatTimeData);

		let sampleBuffer = this.preFloatTimeData;
		// Compute peak instantaneous power over the interval.
		let peakInstantaneousPower = 0;
		for (let i = 0; i < sampleBuffer.length; i++) {
			const power = sampleBuffer[i] ** 2;
			peakInstantaneousPower = Math.max(power, peakInstantaneousPower);
		}
		const peakInstantaneousPowerDecibels = this.convertGainToDecibels(peakInstantaneousPower);

		this.preMetering = peakInstantaneousPowerDecibels;
	}

	updatePostMeters() {
		// Get the new frequency data
		this.postAnalyserNode.getFloatTimeDomainData(this.postFloatTimeData);
		let sampleBuffer = this.postFloatTimeData;
		// Compute peak instantaneous power over the interval.
		let peakInstantaneousPower = 0;
		for (let i = 0; i < sampleBuffer.length; i++) {
			const power = sampleBuffer[i] ** 2;
			peakInstantaneousPower = Math.max(power, peakInstantaneousPower);
		}
		const peakInstantaneousPowerDecibels = this.convertGainToDecibels(peakInstantaneousPower);

		this.postMetering = peakInstantaneousPowerDecibels;
	}

	convertDecibelsToGain(db) {
		return Math.pow(10, (db / 20));
	}

	convertGainToDecibels(gain) {
		return 20 * Math.log10( gain );
	}
}